// src/pages/AddOrder.js

import React, { useEffect, useState, useContext } from 'react';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import {
  Typography,
  Paper,
  Button,
  Grid,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
  TextField,
  MenuItem,
  Select,
  Switch
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { addOrder, uploadOrderAttachment } from '../services/orderService';
import { getProductsByOrgId } from '../services/productService';
import { getCustomers } from '../services/customerService';
import { getSuppliers } from '../services/supplierService';
import { createLookupValue } from '../services/lookupService';
// import { styled } from '@mui/material/styles';
import { AttachFile } from '@mui/icons-material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import orderSchema from '../models/orderSchema';
import DynamicForm from '../components/DynamicForm';
import EmiConfigurationForm from '../components/EmiConfigurationForm';
import { OrgContext } from '../contexts/OrgContext';
import { CurrencyContext } from '../contexts/CurrencyContext';
import BackButton from '../components/BackButton';
import CloseIcon from '@mui/icons-material/Close';
import formatDateToStandard from '../utils/DateFormatter';
import CustomDatePicker from '../components/CustomDatePicker';

const AddOrder = () => {
  const location = useLocation(); // Add this line to get the location object
  
  // Set default values ONLY if we have state from location
  const defaultValues = location.state || {};

  const methods = useForm({
    mode: 'onChange', // Change from 'onSubmit' to 'onChange'
    defaultValues, // This will be empty if no location.state
  });
  const { handleSubmit, control, formState: { errors }, setValue } = methods; 
  const orderType = useWatch({ control, name: "order_type" }); // Replace methods.watch with useWatch to ensure live updates

  const navigate = useNavigate();
  const { orgId } = useContext(OrgContext); // Access orgId from OrgContext
  const { currency } = useContext(CurrencyContext); // Access currency from CurrencyContext

  const [inventoryOptions, setInventoryOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]); // Add state for supplier options
  const [selectedCustomer, setSelectedCustomer] = useState(null); // Add state for selected customer
  const [loading, setLoading] = useState(false);
  const [addedOrder, setAddedOrder] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [differentDeliveryAddress, setDifferentDeliveryAddress] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [paymentType, setPaymentType] = useState('cash'); // Add state for payment type
  const [paymentDueDate, setPaymentDueDate] = useState(''); // Add state for payment due date
  const [selectedFile, setSelectedFile] = useState(null); // Add state for the selected file
  const [filePreview, setFilePreview] = useState(null); // Add state for file preview
  const [fileError, setFileError] = useState(''); // Add state for file error message
  const [accountsReceivable, setAccountsReceivable] = useState('');
  const [accountsPayable, setAccountsPayable] = useState('');
  const [accountsError, setAccountsError] = useState(''); // Add state for accounts validation error
  const [priceLabels, setPriceLabels] = useState({ original: "Original Price", final: "Final Price" }); // New state for price labels, updated dynamically based on order type
  const [isEmi, setIsEmi] = useState(false);
  const [emiData, setEmiData] = useState({
    duration: '',
    frequency: 'monthly',
    interestRate: '',
    startDate: '',
    calculatedEmi: 0,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (orderType === "Purchase") {
      setPriceLabels({ original: "Original Cost", final: "Final Cost" });
    } else {
      setPriceLabels({ original: "Original Price", final: "Final Price" });
    }
  }, [orderType]);

  useEffect(() => {
    const items = methods.getValues("product_line_items") || [];
    if (items.length) {
      const updatedItems = items.map(item => {
        const updatedUnit = orderType === "Purchase"
          ? parseFloat(item.unit_cost || item.unit_price)   // use unit_cost if available
          : parseFloat(item.unit_price);
        return {
          ...item,
          unit_price: updatedUnit,
          original_unit_price: updatedUnit,
          total_price: updatedUnit * parseInt(item.quantity || "1", 10)
        };
      });
      methods.setValue("product_line_items", updatedItems);
    }
  }, [orderType, methods]);

  const currencySymbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    AUD: 'A$',
    CAD: 'C$',
    CHF: 'CHF',
    CNY: '¥',
    INR: '₹',
    JPY: '¥',
    BDT: '৳',
  };

  const symbol = currencySymbols[currency] || currency;

  useEffect(() => {
    if (orgId) {
      const intOrgId = parseInt(orgId); // Ensure orgId is parsed as an integer

      Promise.all([
        // Fetch all products by setting limit=0
        getProductsByOrgId(intOrgId, { page: 1, limit: 0, sortBy: 'product_name', sortOrder: 'ASC' }),
        getCustomers(intOrgId),
        getSuppliers(intOrgId)
      ]).then(([productsResponse, customersResponse, suppliersResponse]) => {
        // Extract data from response objects
        const products = productsResponse.data || productsResponse;
        const customers = customersResponse.data || customersResponse;
        const suppliers = suppliersResponse.data || suppliersResponse;
        
        setInventoryOptions(products.map(product => ({
          label: product.product_name,
          value: product.id.toString(), // Ensure value is a string
          sku: product.sku,
          unit_price: product.current_unit_price,
          unit_cost: product.current_unit_cost,
          scannerReference: product.scanner_reference, // Add scanner_reference field
          meta: product.meta || null,
        })));
        
        setCustomerOptions(customers);
        setSupplierOptions(suppliers); // Make sure suppliers are set correctly
      });
    }
  }, [orgId]);

  // Add debug effect to monitor state changes
  useEffect(() => {
    console.log('addedOrder state changed:', addedOrder);
  }, [addedOrder]);

  // Modify the initialization useEffect
  useEffect(() => {
    // Only populate form with defaults if we came from a specialized button
    if (location.state) {
      Object.entries(location.state).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          setValue(key, value);
        }
      });
    }
    // Do not set defaults if we came from the "Add Order" button
  }, [location.state, setValue]);

  // Improve the calculateOrderTotals function to ensure it always returns valid numbers
  const calculateOrderTotals = (lineItems, taxPercentage) => {
    // Guard against invalid inputs
    if (!lineItems || !Array.isArray(lineItems) || lineItems.length === 0) {
      return {
        subtotal: 0,
        taxAmount: 0,
        total: 0
      };
    }

    // Calculate subtotal, ensuring each item has valid numbers
    const subtotal = lineItems.reduce((sum, item) => {
      // Use total_price if available, otherwise calculate from unit_price * quantity
      let itemTotal = parseFloat(item.total_price || 0);
      if (itemTotal === 0 && item.unit_price && item.quantity) {
        itemTotal = parseFloat(item.unit_price) * parseInt(item.quantity, 10);
      }
      return sum + (isNaN(itemTotal) ? 0 : itemTotal);
    }, 0);

    // Calculate tax amount
    const validTaxPercentage = !isNaN(parseFloat(taxPercentage)) ? parseFloat(taxPercentage) : 0;
    const taxAmount = (subtotal * (validTaxPercentage / 100));
    
    // Calculate total
    const total = subtotal + taxAmount;

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      taxAmount: parseFloat(taxAmount.toFixed(2)),
      total: parseFloat(total.toFixed(2))
    };
  };

  const renderOrderDetails = (order) => {
    const originalLabel = order.order_type === "Purchase" ? "Original Cost:" : "Original Unit Price:";
    const finalLabel = order.order_type === "Purchase" ? "Final Cost:" : "Final Unit Price:";
    return (
      <Box sx={{ 
        '& .MuiTypography-root': { 
          mb: 1,
          color: 'text.primary' 
        }
      }}>
        <Typography><strong>Order ID:</strong> {order.id}</Typography>
        <Typography><strong>Order Type:</strong> {order.order_type}</Typography>
        <Typography><strong>Order Date:</strong> {formatDateToStandard(order.order_date)}</Typography>
        <Typography><strong>Order Status:</strong> {order.order_status}</Typography>
        <Typography><strong>Total Amount:</strong> {symbol}{parseFloat(order.amount).toFixed(2)}</Typography>
        
        {order.supplier_id && (
          <Typography><strong>Supplier ID:</strong> {order.supplier_id}</Typography>
        )}
        
        {order.customer_id && (
          <Typography><strong>Customer ID:</strong> {order.customer_id}</Typography>
        )}

        {order.meta?.deliveryAddress && (
          <Typography><strong>Delivery Address:</strong> {order.meta.deliveryAddress}</Typography>
        )}

        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Line Items:</Typography>
        {order.lineItems.map((item, index) => (
          <Box key={index} sx={{ ml: 2, mb: 2 }}>
            <Typography><strong>Product:</strong> {item.product.product_name}</Typography>
            <Typography><strong>Quantity:</strong> {item.quantity}</Typography>
            <Typography><strong>{originalLabel}</strong> {symbol}{parseFloat(item.original_unit_price).toFixed(2)}</Typography>
            {item.discount_type !== 'none' && (
              <Typography>
                <strong>Discount Applied:</strong> {item.discount_type === 'percentage' ? `${item.discount_value}%` : `${symbol}${item.discount_value}`}
              </Typography>
            )}
            <Typography><strong>{finalLabel}</strong> {symbol}{parseFloat(item.unit_price).toFixed(2)}</Typography>
            <Typography><strong>Total Price:</strong> {symbol}{parseFloat(item.total_price).toFixed(2)}</Typography>
            <Typography><strong>SKU:</strong> {item.product.sku}</Typography>
          </Box>
        ))}
        <Typography><strong>Subtotal:</strong> {symbol}{parseFloat(order.meta?.subtotal || 0).toFixed(2)}</Typography>
        {order.meta?.tax_percentage && (
          <>
            <Typography>
              <strong>Tax ({order.meta.tax_percentage}%):</strong> {symbol}{parseFloat(order.meta?.tax_amount || 0).toFixed(2)}
            </Typography>
          </>
        )}
        <Typography><strong>Total Amount:</strong> {symbol}{parseFloat(order.amount).toFixed(2)}</Typography>
      </Box>
    );
  };

  // Update handleFileSelect to handle file selection and validation
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setSelectedFile(null);
      setFilePreview(null);
      return;
    }

    // Check file size (max 2MB)
    const MAX_SIZE = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > MAX_SIZE) {
      setFileError('File size exceeds the maximum limit of 2MB.');
      setSelectedFile(null);
      setFilePreview(null);
      return;
    }

    // Check file type (images and PDF only)
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setFileError('Only images (JPEG, PNG, GIF, WebP) and PDF files are allowed.');
      setSelectedFile(null);
      setFilePreview(null);
      return;
    }

    setFileError('');
    setSelectedFile(file);

    // Generate preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      // For PDFs, just show an icon
      setFilePreview(null);
    }
  };

  // Add a handler to cancel the selected file
  const handleCancelFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setFileError('');
    // Also clear the input value if needed
    const input = document.getElementById('file-upload');
    if (input) input.value = '';
  };

  // Update onSubmit to save new tax percentage values as fallback
  const onSubmit = async (data) => {
    // Get the line items from the form
    const lineItems = data.product_line_items || [];
    if (lineItems.length === 0) {
      setErrorMessage('Please add at least one product to the order.');
      return;
    }

    // Handle tax percentage - treat null, undefined, empty string as 0
    let taxPercentage = data.tax_percentage;
    if (taxPercentage === null || taxPercentage === undefined || taxPercentage === '') {
      taxPercentage = 0;
    } else {
      taxPercentage = parseFloat(taxPercentage);
      if (isNaN(taxPercentage)) {
        taxPercentage = 0;
      }
    }
    
    // Save tax percentage to lookup table if it's new and not 0
    if (taxPercentage > 0) {
      try {
        console.log('Saving tax percentage from form submit:', taxPercentage);
        await createLookupValue(orgId, 'tax_percentage', taxPercentage.toString());
        console.log('Successfully saved tax percentage from form submit');
      } catch (error) {
        // Fail silently - the order still proceeds even if saving the tax percentage fails
        console.error('Failed to save new tax percentage from form submit:', error);
      }
    }

    const totals = calculateOrderTotals(lineItems, taxPercentage);

    // Start by creating meta object for the order
    let meta = {
      tax_percentage: taxPercentage,
      tax_amount: totals.taxAmount,
      subtotal: totals.subtotal,
      deliveryAddress: differentDeliveryAddress ? deliveryAddress : null,
      is_emi: isEmi,
      emi_data: isEmi ? emiData : null,
    };

    // Add AR/AP payment due date to meta if credit is present
    if (paymentType === 'credit') {
      if (data.order_type === 'Sale') {
        meta.arPaymentDueDate = paymentDueDate;
      } else if (data.order_type === 'Purchase') {
        meta.apPaymentDueDate = paymentDueDate;
      }
    }

    // If there's a file selected, upload it first
    if (selectedFile) {
      try {
        setLoading(true);
        const uploadResult = await uploadOrderAttachment(selectedFile, orgId);
        // Add the file info to the meta object
        meta.attachment = uploadResult;
      } catch (error) {
        console.error('Error uploading file:', error);
        setErrorMessage('Failed to upload attachment. Please try again.');
        setLoading(false);
        return;
      }
    }

    // Prepare the order data
    const processedData = {
      org_id: parseInt(orgId),
      order_type: data.order_type,
      order_date: data.order_date,
      delivery_date: data.delivery_date,
      order_status: data.order_status,
      customer_id: data.order_type !== 'Purchase' ? data.customer_id || undefined : undefined,
      supplier_id: data.order_type === 'Purchase' ? data.supplier_id || undefined : undefined,
      line_items: data.product_line_items.map(item => {
        // Use the original_unit_price, discount_type, and discount_value from the item
        const originalPrice = parseFloat(item.original_unit_price ?? item.unit_price);
        const quantity = parseInt(item.quantity, 10);
        const discountType = item.discount_type || 'none';
        const discountValue = item.discount_value ? parseFloat(item.discount_value) : 0;
        // Calculate discounted price
        let unit = originalPrice;
        if (discountType === 'percentage' && discountValue) {
          unit = originalPrice * (1 - discountValue / 100);
        } else if (discountType === 'amount' && discountValue) {
          unit = Math.max(0, originalPrice - discountValue);
        }
        return {
          product_id: parseInt(item.product_id, 10),
          quantity: quantity,
          unit_price: unit,
          total_price: unit * quantity,
          original_unit_price: originalPrice,
          discount_type: discountType,
          discount_value: discountValue
        };
      }),
      amount: totals.total,
      meta: meta,
      payment_type: paymentType,
      payment_due_date: paymentType !== 'cash' ? paymentDueDate : null,
      accounts_receivable: paymentType === 'credit' && data.order_type === 'Sale' 
        ? parseFloat(accountsReceivable || totals.total)
        : null,
      accounts_payable: paymentType === 'credit' && data.order_type === 'Purchase' 
        ? parseFloat(accountsPayable || totals.total)
        : null,
      is_emi: isEmi,
      emi_duration_months: isEmi ? emiData.duration : null,
      emi_frequency: isEmi ? emiData.frequency : null,
      emi_interest_rate: isEmi ? emiData.interestRate : null,
      emi_start_date: isEmi && emiData.startDate ? emiData.startDate : null,
    };

    setLoading(true);
    try {
      const response = await addOrder(processedData);
      console.log('Order added successfully:', response);
      // Only reset form on successful submission
      methods.reset();
      setDifferentDeliveryAddress(false);
      setDeliveryAddress('');
      setPaymentType('cash');
      setPaymentDueDate('');
      setSelectedFile(null);
      setFilePreview(null);
      setAccountsReceivable('');
      setAccountsPayable('');
      setAddedOrder(response);
    } catch (error) {
      console.error('Error adding order:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to add order. Please try again.');
      // Don't reset form on error - keep all data intact including product line items
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewOrder = () => {
    setAddedOrder(null);
    // Reset all form data and state when explicitly adding new order
    methods.reset();
    setDifferentDeliveryAddress(false);
    setDeliveryAddress('');
    setPaymentType('cash');
    setPaymentDueDate('');
    setSelectedFile(null);
    setFilePreview(null);
    setAccountsReceivable('');
    setAccountsPayable('');
    setIsEmi(false);
    setEmiData({
      duration: '',
      frequency: 'monthly',
      interestRate: '',
      startDate: '',
      calculatedEmi: 0,
    });
  };

  // Reset AR/AP when payment type changes
  useEffect(() => {
    if (paymentType === 'cash') {
      setAccountsReceivable('');
      setAccountsPayable('');
      setAccountsError(''); // Clear any error when switching to cash
    }
  }, [paymentType]);

  // Validate accounts receivable/payable when they change
  useEffect(() => {
    if (paymentType === 'cash') return;
    
    const orderTypeValue = methods.watch('order_type');
    const lineItems = methods.watch('product_line_items') || [];
    var taxPercentage = methods.watch('tax_percentage') ? parseFloat(methods.watch('tax_percentage')) : 0;
    
    // Only proceed if there are line items
    if (!lineItems.length) {
      setAccountsError('');
      return;
    }
    
    // Calculate total using the same function as in submit to ensure consistency
    const totals = calculateOrderTotals(lineItems, taxPercentage);
    
    // Only validate if there are line items and a total
    if (totals.total > 0) {
      if (orderTypeValue === 'Sale' && accountsReceivable) {
        if (parseFloat(accountsReceivable) > totals.total) {
          setAccountsError(`Accounts Receivable cannot exceed the total amount (${symbol}${totals.total.toFixed(2)})`);
        } else {
          setAccountsError('');
        }
      } else if (orderTypeValue === 'Purchase' && accountsPayable) {
        if (parseFloat(accountsPayable) > totals.total) {
          setAccountsError(`Accounts Payable cannot exceed the total amount (${symbol}${totals.total.toFixed(2)})`);
        } else {
          setAccountsError('');
        }
      }
    }
  }, [accountsReceivable, accountsPayable, methods, paymentType, symbol]);

  // Reset AR/AP when payment type changes or when line items/tax changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (paymentType === 'cash') {
      setAccountsReceivable('');
      setAccountsPayable('');
      setAccountsError(''); 
      return;
    } 
    
    const orderTypeValue = methods.watch('order_type');
    const lineItems = methods.watch('product_line_items') || [];
    const taxPercentage = methods.watch('tax_percentage') ? parseFloat(methods.watch('tax_percentage')) : 0;
    
    // Only proceed if there are line items
    if (!lineItems.length) return;
    
    const totals = calculateOrderTotals(lineItems, taxPercentage);
    
    // Only update if we have a valid total
    if (totals.total > 0) {
      if (paymentType === 'credit') {
        // For credit payment type, set AR/AP to the total amount
        if (orderTypeValue === 'Sale') {
          setAccountsReceivable(totals.total.toString());
        } else if (orderTypeValue === 'Purchase') {
          setAccountsPayable(totals.total.toString());
        }
      }
    }
  }, [paymentType, methods]);

  // Add a function to render AR/AP fields based on order type and payment type
  const renderAccountsFields = () => {
    if (paymentType !== 'cash') {
      const orderTypeValue = methods.watch('order_type');
      const lineItems = methods.watch('product_line_items') || [];
      
      // Only render if there are line items
      if (!lineItems.length) {
        return (
          <Typography color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            Add product line items first to enable credit amount fields
          </Typography>
        );
      }
      
      // Declare and reuse the taxPercentage variable
      const taxPercentage = methods.watch('tax_percentage') ? parseFloat(methods.watch('tax_percentage')) : 0;
      
      // Calculate total using the same function as in submit to ensure consistency
      const totals = calculateOrderTotals(lineItems, taxPercentage);
      const total = totals.total || 0; // Ensure we have a default value if calculation returns NaN
      
      // Format the total amount for display, ensuring it's a valid number
      const formattedTotal = !isNaN(total) ? `${symbol}${total.toFixed(2)}` : `${symbol}0.00`;
      
      const fieldValue = orderTypeValue === 'Sale' 
        ? accountsReceivable 
        : accountsPayable;
      
      if (orderTypeValue === 'Sale') {
        return (
          <TextField
            label="Accounts Receivable"
            type="number"
            fullWidth
            value={fieldValue}
            onChange={(e) => setAccountsReceivable(e.target.value)}
            sx={{ mt: 2 }}
            InputProps={{
              startAdornment: (
                <Box component="span" sx={{ mr: 1 }}>
                  {symbol}
                </Box>
              ),
            }}
            placeholder={`Enter Credit Amount`}
            helperText={accountsError || "Amount to be received in the future (leave empty for full amount)"}
            error={!!accountsError}
            inputProps={{ 
              max: total,
              min: 0,
              step: "0.01"
            }}
          />
        );
      } else if (orderTypeValue === 'Purchase') {
        return (
          <TextField
            label="Accounts Payable"
            type="number"
            fullWidth
            value={fieldValue}
            onChange={(e) => setAccountsPayable(e.target.value)}
            sx={{ mt: 2 }}
            InputProps={{
              startAdornment: (
                <Box component="span" sx={{ mr: 1 }}>
                  {symbol}
                </Box>
              ),
            }}
            placeholder={`Enter amount (up to ${formattedTotal})`}
            helperText={accountsError || "Amount to be paid in the future (leave empty for full amount)"}
            error={!!accountsError}
            inputProps={{ 
              max: total,
              min: 0,
              step: "0.01"
            }}
          />
        );
      }
    }
    return null;
  };

  return (
    <Paper
      sx={{
        p: 3,
        maxWidth: 900,
        mx: 'auto',
        backgroundColor: '#1e1e1e',
        color: 'text.primary',
      }}
    >
      <BackButton />
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setErrorMessage('')} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : addedOrder ? (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ color: 'success.main' }}>
            Order Added Successfully
          </Typography>
          <Box sx={{ 
            my: 3, 
            p: 2, 
            backgroundColor: 'background.paper',
            borderRadius: 1,
          }}>
            {renderOrderDetails(addedOrder)}
          </Box>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleAddNewOrder} 
            sx={{ mt: 2, mr: 2 }}
          >
            Add New Order
          </Button>
          <Button 
            variant="contained" 
            color="secondary" 
            onClick={() => navigate(`/${orgId}/orders`)} // Ensure proper redirection to org's orders page
            sx={{ mt: 2 }}
          >
            View Orders
          </Button>
        </Box>
      ) : (
        <>
          <Typography variant="h5" gutterBottom>
            Add Order
          </Typography>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <DynamicForm
                schema={orderSchema}
                control={control}
                errors={errors}
                inventoryOptions={inventoryOptions}
                customerOptions={customerOptions}
                supplierOptions={supplierOptions}
                selectedCustomer={selectedCustomer}
                setSelectedCustomer={setSelectedCustomer}
                priceLabels={priceLabels}  // <-- pass updated priceLabels
              />
              
              {/* Delivery Address Section */}
              <Box sx={{ mt: 3, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={differentDeliveryAddress}
                      onChange={(e) => setDifferentDeliveryAddress(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Different Delivery Address"
                />
                
                {differentDeliveryAddress && (
                  <TextField
                    label="Delivery Address"
                    multiline
                    rows={3}
                    fullWidth
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    sx={{ mt: 1 }}
                    placeholder="Enter delivery address"
                    variant="outlined"
                  />
                )}
              </Box>
              
              {/* Payment Details Section */}
              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography variant="h6">Payment Details</Typography>
                <Select
                  value={paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="credit">Credit</MenuItem>
                </Select>
                {paymentType !== 'cash' && (
                  <CustomDatePicker
                    label="Payment Due Date"
                    value={paymentDueDate}
                    onChange={setPaymentDueDate}
                    sx={{ mt: 2 }}
                    required={paymentType !== 'cash'}
                  />
                )}
                {paymentType === 'credit' && (
                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isEmi}
                          onChange={(e) => setIsEmi(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Enable EMI (Equated Monthly Installments)"
                    />
                  </Box>
                )}
                {renderAccountsFields()}
                {paymentType === 'credit' && isEmi && (
                  <EmiConfigurationForm
                    creditAmount={methods.watch('order_type') === 'Sale' ? accountsReceivable : accountsPayable}
                    onEmiChange={setEmiData}
                    emiData={emiData}
                    currencySymbol={symbol}
                  />
                )}
              </Box>

              {/* File Upload Section - Updated UI */}
              <Box sx={{ mt: 3, mb: 2 }}>
                <input
                  accept="image/*,application/pdf"
                  style={{ display: 'none' }}
                  id="file-upload"
                  type="file"
                  onChange={handleFileSelect}
                />
                <label htmlFor="file-upload" style={{ display: 'inline-block' }}>
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<AttachFile />}
                    sx={{
                      mt: 1,
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': { backgroundColor: 'primary.dark' },
                      textTransform: 'none',
                      minWidth: 180,
                      justifyContent: 'flex-start'
                    }}
                    disabled={!!selectedFile}
                  >
                    {selectedFile
                      ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          {selectedFile.type === 'application/pdf'
                            ? <PictureAsPdfIcon sx={{ mr: 1, color: '#f44336' }} />
                            : <AttachFile sx={{ mr: 1 }} />}
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'inherit',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1
                            }}
                          >
                            {selectedFile.name}
                          </Typography>
                        </Box>
                      )
                      : 'Upload Document (Optional)'
                    }
                  </Button>
                </label>
                {selectedFile && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    sx={{
                      ml: 2,
                      mt: 1,
                      minWidth: 0,
                      padding: '6px',
                      borderRadius: '50%',
                      verticalAlign: 'middle'
                    }}
                    onClick={handleCancelFile}
                  >
                    <CloseIcon />
                  </Button>
                )}
                {selectedFile && !filePreview && (
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                    <PictureAsPdfIcon sx={{ mr: 1, color: '#f44336' }} />
                    <Typography variant="body2" color="text.secondary">
                      {selectedFile.name} (PDF)
                    </Typography>
                  </Box>
                )}
                {filePreview && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      File Preview:
                    </Typography>
                    <img 
                      src={filePreview} 
                      alt="File preview" 
                      style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4, marginTop: 8 }}
                    />
                  </Box>
                )}
                {fileError && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {fileError}
                  </Typography>
                )}
              </Box>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} textAlign="right">
                  <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary"
                    disabled={!!accountsError} // Disable submit button if there's an accounts error
                  >
                    Add Order
                  </Button>
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </>
      )}
    </Paper>
  );
};
export default AddOrder;
